import React, { useEffect, useState, useCallback } from 'react';
import { LayoutList, Columns } from 'lucide-react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge, Card, FilterBar, Avatar } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState } from '../../features/shared/PageStates';
import { STAGE_LABELS, PIPELINE_KANBAN_STAGES, STAGE_BADGE } from '../../lib/statusMaps';
import { formatZAR, initials } from '../../lib/format';

function RiskBadge({ deal }) {
  if (deal.above_75_limit) return <Badge type="danger">Above 75%</Badge>;
  if (deal.below_min_margin) return <Badge type="warn">Below margin</Badge>;
  if (deal.end_client_rating === 'red') return <Badge type="danger">End-client Red</Badge>;
  if (deal.end_client_rating === 'amber') return <Badge type="warn">End-client Amber</Badge>;
  if (deal.director_screening === 'blacklisted') return <Badge type="danger">Blacklisted</Badge>;
  if (deal.director_screening === 'possible_match') return <Badge type="warn">Director match</Badge>;
  return null;
}

export function DealPipeline({ onNavigate, selectedIds, showToast, activePage }) {
  const [deals, setDeals] = useState([]);
  const [view, setView] = useState('table');
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    const stageByPage = {
      'dm-ic': { stage: 'ic_review' },
      'dm-finance': { stage: 'finance_handover' },
      'dm-repayment': { stage: 'repayment_monitoring' },
      'dm-reconciliation': { stage: 'reconciliation' },
      'dm-tl-qc': { stage: 'team_lead_qc' },
      'dm-capital': { stage: 'capital_allocation' },
      'dm-projects': { stage: 'delivery_monitoring' },
    };
    if (activePage === 'dm-pipeline') {
      setFilters(selectedIds?.dmFilter || {});
    } else if (stageByPage[activePage]) {
      setFilters(stageByPage[activePage]);
    }
  }, [activePage, selectedIds?.dmFilter]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = Object.fromEntries(
      Object.entries({ ...filters, search }).filter(([, v]) => v != null && v !== '')
    );
    dealMgmt.pipeline(params)
      .then(res => setDeals(res.deals || []))
      .catch(e => {
        setError(e.message || 'Could not load deals');
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  }, [filters, search, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDeal = (id, tab) => onNavigate('dm-deal-detail', { dmDealId: id, dmTab: tab });

  const onDropStage = async (toStage) => {
    if (!dragId || moving) return;
    const deal = deals.find(d => d.id === dragId);
    if (!deal || deal.stage === toStage) {
      setDragId(null);
      setDragOver(null);
      return;
    }
    setMoving(true);
    try {
      await dealMgmt.updateStage(dragId, { stage: toStage });
      showToast?.(`Moved to ${STAGE_LABELS[toStage] || toStage}`);
      load();
    } catch (e) {
      const msg = e.message || 'Stage change blocked';
      showToast?.(msg, 'error');
    } finally {
      setMoving(false);
      setDragId(null);
      setDragOver(null);
    }
  };

  return (
    <div>
      <FilterBar>
        <input
          className="field-input"
          placeholder="Search deals…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        <select
          className="field-input"
          value={filters.stage || ''}
          onChange={e => setFilters(f => ({ ...f, stage: e.target.value || undefined }))}
        >
          <option value="">All stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="field-input"
          value={filters.service_type || ''}
          onChange={e => setFilters(f => ({ ...f, service_type: e.target.value || undefined }))}
        >
          <option value="">All services</option>
          <option value="po_funding">PO Funding</option>
          <option value="invoice_discounting">Invoice Discounting</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className={`btn btn-sm ${view === 'table' ? 'btn-p' : 'btn-o'}`} onClick={() => setView('table')}>
            <LayoutList size={13} style={{ marginRight: 4 }} /> Table
          </button>
          <button className={`btn btn-sm ${view === 'kanban' ? 'btn-p' : 'btn-o'}`} onClick={() => setView('kanban')}>
            <Columns size={13} style={{ marginRight: 4 }} /> Kanban
          </button>
          <button className="btn btn-p btn-sm" onClick={() => onNavigate('dm-new-deal')}>+ New deal</button>
        </div>
      </FilterBar>

      {error && <ErrorState message={error} onRetry={load} />}
      {loading && <LoadingState label="Loading pipeline…" />}

      {!loading && !error && deals.length === 0 && (
        <EmptyState
          title="No deals found"
          sub="Adjust filters or create a new deal."
          action={() => onNavigate('dm-new-deal')}
          actionLabel="+ New deal"
        />
      )}

      {!loading && !error && view === 'kanban' && deals.length > 0 && (
        <div className="kanban-board">
          {PIPELINE_KANBAN_STAGES.map(stage => (
            <div
              key={stage}
              className={`kanban-col ${dragOver === stage ? 'drag-over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(stage); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); onDropStage(stage); }}
            >
              <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 8, color: 'var(--brand)' }}>
                {STAGE_LABELS[stage]}
                <span style={{ marginLeft: 6, color: 'var(--text3)', fontWeight: 500 }}>
                  {deals.filter(d => d.stage === stage).length}
                </span>
              </div>
              {deals.filter(d => d.stage === stage).map(d => (
                <div
                  key={d.id}
                  className={`kanban-card ${dragId === d.id ? 'dragging' : ''}`}
                  draggable={!moving}
                  onDragStart={() => setDragId(d.id)}
                  onDragEnd={() => { setDragId(null); setDragOver(null); }}
                  onClick={() => openDeal(d.id)}
                >
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{d.client_company_name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>{d.deal_ref}</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>{formatZAR(d.capital_required)}</div>
                  <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Badge type="neutral">{d.service_type === 'invoice_discounting' ? 'ID' : 'PO'}</Badge>
                    <RiskBadge deal={d} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {!loading && !error && view === 'table' && deals.length > 0 && (
        <Card>
          <table className="tbl">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Client</th>
                <th>End-client</th>
                <th>Service</th>
                <th>Capital</th>
                <th>Margin</th>
                <th>Stage</th>
                <th>Sub-status</th>
                <th>DM</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {deals.map(d => (
                <tr key={d.id} className="clickable" onClick={() => openDeal(d.id)}>
                  <td className="fw mono">{d.deal_ref}</td>
                  <td>{d.client_company_name}</td>
                  <td>{d.end_client_name || '—'}</td>
                  <td><Badge type="neutral">{d.service_type === 'invoice_discounting' ? 'ID' : 'PO'}</Badge></td>
                  <td className="r">{formatZAR(d.capital_required)}</td>
                  <td className="r">
                    {d.service_type === 'invoice_discounting'
                      ? (d.above_75_limit ? <Badge type="danger">Above 75%</Badge> : '75% max')
                      : (d.profit_margin_pct != null ? `${parseFloat(d.profit_margin_pct).toFixed(1)}%` : '—')}
                  </td>
                  <td><Badge type={STAGE_BADGE[d.stage] || 'neutral'}>{STAGE_LABELS[d.stage] || d.stage}</Badge></td>
                  <td><Badge type="neutral">{(d.sub_status || '').replace(/_/g, ' ')}</Badge></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {d.deal_manager_name && <Avatar initials={initials(d.deal_manager_name)} size={20} />}
                      <span style={{ fontSize: 11 }}>{d.deal_manager_name || '—'}</span>
                    </div>
                  </td>
                  <td><RiskBadge deal={d} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

export default DealPipeline;
